import type { AdminHostWithType } from "@/admin/hosts/hostQuery.ts";
import type { BadgeProps } from "@/components/Badge.tsx";
import type { EnumProps } from "@/components/EnumBadge.tsx";
import type { AdminHostUpdateBody } from "@warrenio/api-spec/spec.oats.gen";

export type StatusType = "AVAILABLE" | "NOT ACCEPTING" | "MAINTENANCE" | "OFFLINE";

export interface StatusTypeItem extends EnumProps {
    color: NonNullable<BadgeProps["color"]>;
    flags: Pick<AdminHostUpdateBody, "is_accepting_workloads" | "is_maintenance" | "is_offline">;
}

export const statusTypes: Record<StatusType, StatusTypeItem> = {
    AVAILABLE: {
        label: "AVAILABLE",
        color: "success",
        description: "Host is considered a valid target for new and migrated VMs. Existing VMs will keep running",
        flags: {
            is_accepting_workloads: true,
            is_maintenance: false,
            is_offline: false,
        },
    },
    "NOT ACCEPTING": {
        label: "NOT ACCEPTING",
        color: "warning",
        description:
            "Host is not available for new VMs, but existing VMs will continue running. Admin will still be able to explicitly migrate VMs to that host",
        flags: {
            is_accepting_workloads: false,
            is_maintenance: false,
            is_offline: false,
        },
    },
    MAINTENANCE: {
        label: "MAINTENANCE",
        color: "error",
        description:
            "All VMs on this host will be automatically migrated to other hosts. Use this if the host is accessible and you want to make it empty",
        flags: {
            is_accepting_workloads: false,
            is_maintenance: true,
            is_offline: false,
        },
    },
    OFFLINE: {
        label: "OFFLINE",
        color: "text",
        description:
            "Host is considered to be unavailable, all VMs on this host will be automatically recreated on other hosts (it is like doing Force Stop and Start to all its VMs). Use only if you are sure that there are no running VMs on that host!",
        flags: {
            is_accepting_workloads: false,
            is_maintenance: false,
            is_offline: true,
        },
    },
};

export function getStatus(item: AdminHostWithType): StatusType {
    if (item.is_offline) {
        return "OFFLINE";
    } else if (item.is_maintenance) {
        return "MAINTENANCE";
    } else if (item.is_accepting_workloads) {
        return "AVAILABLE";
    } else {
        return "NOT ACCEPTING";
    }
}
